import { DEFAULT_FONT_FAMILY, fontCssString, fontSource, primaryFontFamily } from "./fonts";

type FontRequest = { fontFamily?: string; fontUrl?: string; fontWeight: number };
type FontResource = { users: number; remove: () => void };
const resources = new Map<string, FontResource>();

/** Share downloads across elements and simultaneous canvas/history previews.
 * Release only when the last rendered element stops using the font. */
export function retainCanvasFont({ fontFamily, fontUrl, fontWeight }: FontRequest) {
  const source = fontSource(fontUrl || "");
  if (!source || typeof document === "undefined") return;
  const family = primaryFontFamily(fontFamily || DEFAULT_FONT_FAMILY);
  const key = JSON.stringify(source.kind === "stylesheet"
    ? [source.url] : [source.url, family, fontWeight]);
  let resource = resources.get(key);
  if (!resource) {
    if (source.kind === "stylesheet") {
      // Only Google's font CSS is allowed; arbitrary remote stylesheets could
      // otherwise change the surrounding application as well as the canvas.
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = source.url;
      link.referrerPolicy = "no-referrer";
      link.dataset.canvasFont = "";
      document.head.appendChild(link);
      resource = { users: 0, remove: () => link.remove() };
    } else {
      if (typeof FontFace === "undefined" || !document.fonts) return;
      const face = new FontFace(family, `url(${fontCssString(source.url)})`, {
        weight: String(fontWeight), display: "swap",
      });
      document.fonts.add(face);
      // Failed fonts leave the element's fallback stack usable. Register before
      // loading so releasing an in-flight request cannot re-add a stale face.
      void face.load().catch(() => {});
      resource = { users: 0, remove: () => { document.fonts.delete(face); } };
    }
    resources.set(key, resource);
  }
  resource.users++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--resource.users === 0) {
      resource.remove();
      resources.delete(key);
    }
  };
}
